import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';
import { Turnstile } from '@marsidev/react-turnstile';

import { logger } from '@/utils/logger';
import { CheckCircle, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function QuestionnaireResponse() {
  const { shareId } = useParams();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const [formData, setFormData] = useState({
    answers: [],
    personalNote: ''
  });

  // Dynamic OG tags, makes shared questionnaire links show a warm,
  // contextual preview instead of the generic home-page card.
  const recipientName = questionnaire?.recipient_name || 'someone special';
  useDocumentMeta({
    title: questionnaire
      ? `Help pick a gift for ${recipientName}, Mementa`
      : 'Gift Questionnaire, Mementa',
    description: questionnaire
      ? `You've been invited to help pick the perfect gift for ${recipientName}. Answer a few questions on Mementa.`
      : 'You have been invited to help pick a thoughtful gift on Mementa.',
    type: 'website',
  });

  const fetchQuestionnaire = useCallback(async () => {
    try {
      const response = await fetch(`${api}/gift-questionnaire/${shareId}`);
      if (response.ok) {
        const data = await response.json();
        setQuestionnaire(data);
        setFormData(prev => ({
          ...prev,
          answers: new Array(data.questions.length).fill('')
        }));
      } else {
        toast.error('Questionnaire not found or no longer active');
      }
    } catch (error) {
      logger.error('Failed to fetch questionnaire:', error);
      toast.error('Failed to load questionnaire');
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    fetchQuestionnaire();
  }, [fetchQuestionnaire]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.answers.some(a => !a.trim())) {
      toast.error('Please answer all questions');
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error('Please wait for the verification check to complete');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${api}/gift-questionnaire/${shareId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          respondent_name: 'Anonymous',
          respondent_email: null,
          answers: formData.answers,
          personal_note: formData.personalNote.trim() || null,
          turnstile_token: turnstileToken
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Response submitted! Thank you!');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit');
      }
    } catch (error) {
      toast.error(error.message);
      logger.error(error);
      // Turnstile tokens are single-use, get a fresh one for the retry.
      if (turnstileRef.current) {
        turnstileRef.current.reset();
        setTurnstileToken(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9FAFB' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9E6752] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9FAFB' }}>
        <div className="text-center">
          <p className="text-xl mb-2" style={{ color: '#20212B' }}>Questionnaire not found</p>
          <p className="text-sm" style={{ color: '#73766A' }}>This link may be invalid or expired</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F9FAFB' }}>
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl p-8 text-center" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#D1FAE5' }}>
              <CheckCircle className="w-8 h-8" style={{ color: '#065F46' }} />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#20212B' }}>Thank You!</h1>
            <p className="text-base" style={{ color: '#73766A' }}>
              Your responses have been recorded and will help find the perfect gift for you!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" style={{ background: 'linear-gradient(135deg, #2D4354 0%, #534145 100%)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with warm introduction */}
        <div className="bg-white rounded-xl p-8 mb-6" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div className="text-center mb-6">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #9E6752 0%, #FED7A5 100%)' }}>
              <Heart className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#20212B' }}>
              Someone Special Wants to Give You a Gift! 🎁
            </h1>
            <div className="max-w-2xl mx-auto">
              <p className="text-lg mb-4" style={{ color: '#534145' }}>
                A friend or family member wants to find you something truly special for your <span className="font-semibold" style={{ color: '#9E6752' }}>{questionnaire.occasion}</span>, but they could use a little help making sure it's perfect.
              </p>
              <p className="text-base mb-4" style={{ color: '#73766A' }}>
                Take a few minutes to share your thoughts below. Your answers will help us recommend gifts that feel like they were made just for you. The more honest and detailed you are, the better we can help find something you'll absolutely love!
              </p>
              <div className="inline-block px-4 py-2 rounded-full" style={{ background: '#FED7A5' }}>
                <p className="text-sm font-medium" style={{ color: '#20212B' }}>
                  💡 Pro tip: There are no wrong answers - just be yourself!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div className="space-y-6">
            {/* Questions - First Person */}
            <div className="border-l-4 pl-4" style={{ borderColor: '#FED7A5' }}>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#20212B' }}>Tell us about yourself!</h3>
              <p className="text-sm mb-6" style={{ color: '#73766A' }}>Be as specific or casual as you like - we're all ears 👂</p>
              
              <div className="space-y-6">
                {questionnaire.questions.map((question, idx) => (
                  <div key={question}>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#20212B' }}>
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full mr-2 text-xs font-bold text-white" style={{ background: '#9E6752' }}>
                        {idx + 1}
                      </span>
                      {question}
                    </label>
                    <textarea
                      value={formData.answers[idx]}
                      onChange={(e) => {
                        const newAnswers = [...formData.answers];
                        newAnswers[idx] = e.target.value;
                        setFormData(prev => ({ ...prev, answers: newAnswers }));
                      }}
                      rows={3}
                      className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all placeholder:text-gray-400"
                      style={{
                        borderColor: '#D1D5DB',
                        background: '#FFFFFF',
                        color: '#20212B',
                        '--tw-ring-color': '#9E6752'
                      }}
                      placeholder="Share your thoughts here..."
                      required
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Optional personal note, recipient can leave a fun message
                or any extra context they didn't tag onto a specific answer.
                Also fed into the AI analyzer as bonus signal. */}
            <div className="border-l-4 pl-4" style={{ borderColor: '#9E6752' }}>
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#20212B' }}>
                Anything else? <span className="font-normal text-sm" style={{ color: '#73766A' }}>(optional)</span>
              </h3>
              <p className="text-sm mb-3" style={{ color: '#73766A' }}>
                Leave a fun message for whoever's shopping, or drop any extra context that didn't fit above. 💌
              </p>
              <textarea
                value={formData.personalNote}
                onChange={(e) => setFormData(prev => ({ ...prev, personalNote: e.target.value }))}
                rows={4}
                className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 transition-all placeholder:text-gray-400"
                style={{
                  borderColor: '#D1D5DB',
                  background: '#FFFFFF',
                  color: '#20212B',
                  '--tw-ring-color': '#9E6752'
                }}
                placeholder="A note, inside joke, color you secretly hate, dream gift wish, whatever you want..."
                data-testid="response-personal-note"
              />
            </div>

            {/* Submit */}
            <div className="pt-6 text-center">
              {TURNSTILE_SITE_KEY && (
                <div className="flex justify-center mb-4" data-testid="questionnaire-turnstile-widget">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onExpire={() => setTurnstileToken(null)}
                    options={{ theme: 'light' }}
                  />
                </div>
              )}
              <Button
                type="submit"
                disabled={submitting}
                className="px-8 py-4 text-lg font-semibold rounded-full hover:scale-105 transition-transform"
                style={{ background: 'linear-gradient(135deg, #9E6752 0%, #FED7A5 100%)', color: 'white' }}
              >
                {submitting ? 'Sending Your Responses...' : '✨ Submit & Help Find the Perfect Gift!'}
              </Button>
              <p className="text-xs mt-3" style={{ color: '#73766A' }}>
                Your answers are confidential and will only be used to find great gift recommendations
              </p>
            </div>
          </div>
        </form>

        {/* Subtle Mementa Promotion Footer */}
        <div className="mt-8 text-center">
          <div className="inline-block bg-white/10 backdrop-blur-sm rounded-xl px-6 py-4 border border-white/20">
            <p className="text-sm text-white/90 mb-2">
              Want to find the perfect gifts for your loved ones too?
            </p>
            <a 
              href="/"
              className="inline-flex items-center gap-2 text-sm font-semibold hover:opacity-80 transition-opacity"
              style={{ color: '#FED7A5' }}
            >
              <span>Try Mementa</span>
              <span>→</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
